import React, { ChangeEvent, Fragment, FunctionComponent, useEffect, useState } from "react";
import * as ReactDOM from 'react-dom/client';
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { Col, Modal, Button, Row, Toast, ToastContainer } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import { propTypes } from "react-bootstrap/esm/Image";
import * as _ from "lodash";
import { debug } from "webpack";
import { ContractRefNoSelectBox } from "./contract-select-box";
import { VehicleEntryTypeControls } from "./vehicle-entry-controls";
import "react-datepicker/dist/react-datepicker.css";
import Select from 'react-select'
import { customSelectStyles } from "./utils/react-select-styles";

export function startPage(rootSelector: string, customerNo: string, extDocNoMandatory: boolean, contractLines: ContractLine[], vehicleTypes: VehicleType[]) {
    const domContainer = document.querySelector(rootSelector);
    if (!domContainer) {
        return;
    }
    const root = ReactDOM.createRoot(domContainer);
    root.render(React.createElement(NewBookingPage, { customerNo, extDocNoMandatory, contractLines, vehicleTypes }));
}

interface ServerDate {
    Year: number;
    Month: number;
    Day: number;
}

interface ContractLine {
    CustomerNo: string;
    ContractNo: string;
    ContractDescription: string;
    ContractLineNo: number;
    ContractRefNo: string;
    MaterialType: string;
    EntryType: string;
    MaterialDescription: string;
    LineDescription: string;
    SoilDeclarNoRequired: boolean;
    BookingType: string;
    StartingDate: ServerDate|null;
    EndingDate: ServerDate|null;
    LineStartingDate: ServerDate|null;
    LineEndingDate: ServerDate|null;
}
interface Props {
    customerNo: string;
    extDocNoMandatory: boolean;
    contractLines: ContractLine[];
    vehicleTypes: VehicleType[];
}

interface Contract
{
    refNo: string;
    description: string;
    startingDate: Date|null;
    endingDate: Date|null;
}

interface VehicleType{
    vehicletypecode: string;
    vehicletypedescription: string;
}

interface MaterialType {
    code: string;
    description: string;
    soilDeclarRequired: boolean;
    contractLineNo: number;
}

interface VehicleEntry {
    vehicleRego: string;
    vehicleType: string;
}

interface SaveBookingRequest {
    CustomerNo: string;
    ContractLineNo: number;
    ContractRefNo: string;
    CustomerRefNo: string;
    SiteCode: string;
    MaterialType: string;
    soilDeclarNo: string;
    BookingDate: string;
    VehicleEntries: VehicleEntry[];
}

interface SaveBookingResponse {
    success: boolean;
    errorMessage: string;
}

const NewBookingPage: FunctionComponent<Props> = ({ customerNo, extDocNoMandatory, contractLines, vehicleTypes }) => {
    const [selectedContractRefNo, setContractRefNo] = useState("");
    const [contractLineNo, setContractLineNo] = useState(0);
    const [selectedBookingDate, setBookingDate] = useState<Date|null>(null);
    const [filteredMaterialTypes, setFilteredMaterialTypes] = useState<MaterialType[]>(filterMaterialTypes(contractLines, selectedContractRefNo, selectedBookingDate));
    const [selectedMaterialType, setMaterialType] = useState<MaterialType>({code:"", description:"", soilDeclarRequired: false, contractLineNo: 0});
    const [contracts, setContracts] = useState(filterContracts(contractLines, selectedBookingDate));
    const [soilDeclarNo, setSoilDeclarNo] = useState("");
    const [customerRef, setCustomerRef] = useState("");
    const [vehicleEntries, setVehicleEntries] = useState<VehicleEntry[]>([ { vehicleRego: "", vehicleType: "" }]);
    const [showToast, setShowToast] = useState(false);
    const [validationError, setValidationError] = useState("");
    const [submitRequest, setSubmitRequest] = useState<{ url: string, requestInfo: RequestInit } | null>(null);

    const emptyMaterialType = { code: "", description: "", soilDeclarRequired: false, contractLineNo: 0};

    const isWeekday = (startDate: Date) => {
        const day = startDate.getDay();
        return day !== 0 ;
      };

      const onChangeBookingDate = (newValue: Date) =>{
        setContracts(filterContracts(contractLines, newValue));
        setFilteredMaterialTypes(filterMaterialTypes(contractLines, selectedContractRefNo, newValue));
        setBookingDate(newValue);
      }

    const onContractRefNoChanged = (newValue: string) => {
        setContractRefNo(newValue);
        const filteredMaterialTypes = filterMaterialTypes(contractLines, newValue, selectedBookingDate);
        setFilteredMaterialTypes(filteredMaterialTypes);
        setMaterialType(emptyMaterialType);
        setContractLineNo(0);
        setSoilDeclarNo("");
    };

    const onChangeMaterialType = (newValue: MaterialType | null) => {
        if (!newValue) {
            return;
        }

        if(newValue.soilDeclarRequired != selectedMaterialType.soilDeclarRequired)
        {
            setSoilDeclarNo("");
        }

        setMaterialType(newValue);
        const contractLine = contractLines.find((contractLine) => contractLine.ContractRefNo == selectedContractRefNo && contractLine.ContractLineNo == newValue.contractLineNo);
        setContractLineNo(contractLine ? contractLine.ContractLineNo : 0);
    }

    const onSetVehicleEntry = (rego: string, vehicleType: string, index: number) => { 
        const newState = vehicleEntries.map((vehicleEntry, i) => {
            if (i === index) {
                return { vehicleRego: rego, vehicleType: vehicleType || "" }
            }
            return vehicleEntry;
        });

        if(index == vehicleEntries.length - 1)
        {
            newState.push({ vehicleRego: "", vehicleType: ""});
        }

        setVehicleEntries(newState);
    }

    const removeLastVehicleEntry = () => {
        if(vehicleEntries.length != 1){
            vehicleEntries.pop();
            setVehicleEntries([...vehicleEntries]);
        }
    }

    const handleSubmit = async (e:any) => {
        e.preventDefault();

        if (!selectedContractRefNo || selectedContractRefNo === "" || !selectedMaterialType || selectedMaterialType.code === "" || !selectedBookingDate) {
            return;
        }

        const BookingDate = new Date(Date.UTC(selectedBookingDate.getFullYear(), selectedBookingDate.getMonth(), selectedBookingDate.getDate())).toJSON();
        const filteredVehicleEntries = vehicleEntries.filter((vehicleEntry)=> vehicleEntry.vehicleRego != "");

        if (filteredVehicleEntries.length === 0) {
            return;
        }

        for (const entry of filteredVehicleEntries) {
            if (!entry.vehicleType || entry.vehicleType === "") {
                return;
            }
        }

        const contractLine:SaveBookingRequest = {
            CustomerNo: customerNo,
            ContractLineNo: contractLineNo,
            ContractRefNo: selectedContractRefNo,
            CustomerRefNo: customerRef,
            SiteCode: "",
            MaterialType: selectedMaterialType.code,
            soilDeclarNo: soilDeclarNo,
            BookingDate: BookingDate,
            VehicleEntries: filteredVehicleEntries,
        }

        const requestInfo = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(contractLine),
        }

        setSubmitRequest({ url: "/api/BookingApi/SaveBooking", requestInfo: requestInfo });
    }

    useEffect(() => {
        if (!submitRequest) {
            return;
        }

        const fetchData = async() => {
            setValidationError("");
            await fetch(submitRequest.url, submitRequest.requestInfo)
                .then((response) => response.json())
                .then((data:SaveBookingResponse) => {
                    console.log(data);
                    if(data.success){
                        resetState();
                        setShowToast(true)
                    } else {
                        setValidationError(data.errorMessage)
                        setShowToast(true)
                    }
                })
                .catch((error) => {
                    console.error(error);
                    setValidationError(error)
                    setShowToast(true)
                });
        };
        fetchData();
    }, [submitRequest]);

    const resetState = () => {
        setContractRefNo("");
        setFilteredMaterialTypes([]);
        setBookingDate(null);
        setCustomerRef("");
        setVehicleEntries([ { vehicleRego: "", vehicleType: "" }]);
        setSoilDeclarNo("");
    }

    return (
        <div className="content-container" id="vehicle-booking">
            <div className="booking-form form">
                <form onSubmit={(e) => {handleSubmit(e)}}>
                    <div className="form-container">
                    <li className="row form-list">
                    <Col md={4} xl={2} className="form-group form-group-3">
                                <label>Booking Date</label>
                                <DatePicker 
                                    dateFormat="dd/MM/yyyy"
                                    onChange={(date:Date) => onChangeBookingDate(date)} 
                                    selected={selectedBookingDate}
                                    minDate={new Date()}
                                    filterDate={isWeekday}       
                                />
                    </Col>
                    <Col md={4} xl={3} className="form-group form-group-4 contracts">
                        <label htmlFor="contract-ref-no">Contract Ref No.</label>
                        <ContractRefNoSelectBox contractRefNos={contracts} selectedContractRefNo={selectedContractRefNo} onContractRefNoChanged={onContractRefNoChanged} />
                    </Col>
                    <Col md={4} xl={3} className="form-group form-group-3" id={`material`}>
                    <label htmlFor="material-type">Material Type</label>
                    <Select<MaterialType> options={filteredMaterialTypes} onChange={(e)=> onChangeMaterialType(e)}
                        getOptionLabel={mt => mt.description}
                        getOptionValue={mt => mt.description}
                        defaultValue={{ code: "", description: "", soilDeclarRequired: false, contractLineNo: 0}}
                        value={filteredMaterialTypes.find(mt => mt.contractLineNo === selectedMaterialType.contractLineNo) || emptyMaterialType}
                        menuPortalTarget={document.body}
                        styles={customSelectStyles} />
                    </Col>
                    <Col md={4} xl={2} className="form-group form-group-3">
                    <label htmlFor="soil-decalr-no">RPI&nbsp;Environment&nbsp;No.</label>
                    <input name="soil-decalr-no" onChange={e =>(setSoilDeclarNo(e.target.value))} value={soilDeclarNo} required={selectedMaterialType.soilDeclarRequired} maxLength={20} disabled={selectedMaterialType.soilDeclarRequired === false}></input>
                    </Col>
                    <Col md={4} xl={2} className="form-group form-group-3">
                    <label htmlFor="customer-ref">CUSTOMER&nbsp;REF&nbsp;#/PO&nbsp;#</label>
                    <input name="customer-ref" onChange={e =>(setCustomerRef(e.target.value))} value={customerRef} required={extDocNoMandatory} maxLength={20}></input>
                    </Col>
                    </li>
                    <Row>
                        <Col>
                            <VehicleEntryTypeControls vehicleEntries={vehicleEntries} vehicleTypes={vehicleTypes} onSetVehicleEntry={onSetVehicleEntry}/>
                            {
                                vehicleEntries.length > 1 ? 
                                (
                                    <a className="add-more" type="button"  onClick={e => removeLastVehicleEntry() }><span className="circle"></span><span>Remove Vehicle</span></a>
                                ) : <></>
                            }
                        </Col>
                    </Row>
                    <div className="form-btn pagination-bottom">
                       <div></div>
                        <div>
                            <button type="submit" className="primary-button" onClick={() => {}}>
                                Submit
                            </button>
                            <button id="cancel-button" type="button" className="secondary-button" onClick={() =>{resetState()}}>Cancel</button>
                        </div>
                    </div>
                    </div>
                </form>
                <ToastContainer className="p-3" position="top-center">
                    <Toast onClose={() => setShowToast(false)} show={showToast} delay={3000} autohide={validationError == ""} style={{backgroundColor: "rgba(255, 255, 255, 1)"}}>
                    <Toast.Header>
                    <img
                        className="rounded me-2"
                        alt=""
                    />
                    <strong className="me-auto">{validationError != "" ? "Failed to create booking" :  "Success"}</strong>
                    <small>now</small>
                    </Toast.Header>
                    <Toast.Body>{validationError != "" ? validationError :  "Booking has been created."}</Toast.Body>
                </Toast>
                </ToastContainer>
            </div>
        
        </div>
    )
}

function filterContracts(contractLines: ContractLine[], bookingDate: Date|null) : Contract[] {
    let contracts = _.sortBy(
        _.uniqBy(
            contractLines.map(contractLine => {return {refNo: contractLine.ContractRefNo, description: contractLine.ContractDescription, startingDate: dateFromServerDate(contractLine.StartingDate), endingDate: dateFromServerDate(contractLine.EndingDate)}}),
            contractLine => contractLine.refNo),
        contractLine => contractLine.refNo);

    if(bookingDate != null){
        return contracts.filter(contract => {
            if (!contract.startingDate || bookingDate < contract.startingDate) {
                return false;
            }
            if (contract.endingDate) {
                if (bookingDate > contract.endingDate) {
                    return false;
                }
            }
            return true;
        })
    }
    return contracts;
} 

function filterMaterialTypes(contractLines: ContractLine[], selectedContractRefNo: string, bookingDate: Date|null): MaterialType[] {
    if (!selectedContractRefNo) {
        return [];
    }

    let matchingContractLines = contractLines.filter(contractLine => {
        if (contractLine.ContractRefNo === selectedContractRefNo) {
            return true;
        }
        return false;
    });

    if(bookingDate != null){
        matchingContractLines = matchingContractLines.filter(contractLine => {
            const lineStartingDate = dateFromServerDate(contractLine.LineStartingDate);
            if (lineStartingDate && bookingDate < lineStartingDate) {
                return false;
            }

            if(contractLine.LineEndingDate != null)
            {
                const lineEndingDate = dateFromServerDate(contractLine.LineEndingDate);
                if (lineEndingDate) {
                    if (bookingDate > lineEndingDate) {
                        return false;
                    }
                }
            } 
            return true;
        })
    }

    return _.sortBy(
        _.uniqBy(
            matchingContractLines.map(currentContractLine => {
                let description = currentContractLine.MaterialDescription;

                const duplicateLines = matchingContractLines.filter(contractLine => contractLine.MaterialType == currentContractLine.MaterialType)

                if (duplicateLines.length > 1) {
                    description = currentContractLine.LineDescription;
                }
                const SoilDeclarNoRequired = currentContractLine.SoilDeclarNoRequired && currentContractLine.EntryType === 'Stocking';

                 return { 
                    code: currentContractLine.MaterialType,
                    description: description, 
                    soilDeclarRequired: SoilDeclarNoRequired, 
                    contractLineNo: currentContractLine.ContractLineNo} 
                })
            , materialType => materialType.description),
        materialType => materialType.code);
}

function dateFromServerDate(input: ServerDate|null) {
    if (!input){
        return null;
    }
    return new Date(input.Year, input.Month - 1, input.Day);
}
